import React, { useEffect, useState } from "react";
import { config } from "shared/config/config";
import { formatTime } from "shared/time/businessTime";
import { downloadResponse } from "../hooks/useSecureConnectors";

const STORAGE_KEY = "lineside.connectorSetupLink";

// The token arrives in the URL fragment, which browsers never send to a server.
export function readSetupLinkToken() {
  const fragment = new URLSearchParams(window.location.hash.slice(1)).get(
    "token",
  );
  if (fragment) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fragment);
    } catch {
      /* Keep using the link when storage is unavailable. */
    }
    window.history.replaceState(null, "", "/connect");
    return fragment;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function linkRequest(path, token) {
  return fetch(`${config.API_URL}/api/secure-connectors/it-link/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    referrerPolicy: "no-referrer",
    body: JSON.stringify({ token }),
  });
}

async function errorMessage(response) {
  const result = await response.json().catch(() => ({}));
  return typeof result.detail === "string"
    ? result.detail
    : "This link could not be opened. Try again.";
}

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
    {children}
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);

export default function ConnectorSetupPage() {
  const [token] = useState(readSetupLinkToken);
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This link is incomplete. Open the full link you were sent.");
      return undefined;
    }
    let active = true;
    linkRequest("preview", token)
      .then(async (response) => {
        if (!response.ok) throw new Error(await errorMessage(response));
        return response.json();
      })
      .then((result) => active && setLink(result))
      .catch((failure) => active && setError(failure.message));
    return () => {
      active = false;
    };
  }, [token]);

  async function download() {
    setBusy(true);
    setError("");
    try {
      const response = await linkRequest("windows-installer", token);
      if (!response.ok) throw new Error(await errorMessage(response));
      await downloadResponse(response, "LineSide-Secure-Connector.zip");
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }

  const guide = link?.guide;
  const requester = link?.workspace_name || "The business that sent this link";

  return (
    <main className="flex min-h-screen justify-center bg-slate-50 p-5 text-slate-900">
      <article className="w-full max-w-2xl space-y-6 rounded-2xl border bg-white p-6 shadow-sm">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-cyan-800">
            LineSide
          </p>
          <h1 className="text-2xl font-semibold">Install the LineSide connector</h1>
          {link?.connector_name && (
            <p className="text-sm text-slate-600">
              {requester} asks you to install this so LineSide can read{" "}
              <strong>{link.connector_name}</strong>. You don't need a LineSide account.
            </p>
          )}
        </header>
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        )}
        {!link && !error && <p className="text-sm text-slate-600">Opening the link…</p>}
        {link?.state === "used" && (
          <p role="status" className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
            This link was already used: the connector was installed on{" "}
            {formatTime(link.used_at)}.{" "}
            {link.connected ? "It is online now." : "It is not reporting to LineSide right now."}
          </p>
        )}
        {link?.state === "expired" && (
          <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            This link has expired or was cancelled. Ask the person who sent it for a new one.
          </p>
        )}
        {link?.state === "waiting" && guide && (
          <>
            <Section title="How it reads the system">
              <p className="text-sm text-slate-700">
                <strong>{guide.route}.</strong> {guide.description}
              </p>
              <p className="text-sm text-slate-700">
                If your policy doesn't allow this route, tell the person who sent you this
                link which route you do allow: the system's own API, or a read-only copy of
                its database. This same link will then give you the right installer.
              </p>
            </Section>
            <Section title="What you need">
              <Bullets
                items={[
                  ...guide.requirements,
                  ...(link.lineside_host ? [`LineSide address to allow: ${link.lineside_host}`] : []),
                ]}
              />
            </Section>
            <Section title="What the installer asks for">
              <Bullets items={guide.asks_for} />
              <p className="text-sm text-slate-700">{guide.least_privilege}</p>
              <p className="text-sm text-slate-700">{guide.credentials}</p>
            </Section>
            <Section title="Install">
              <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
                <li>On that computer, download the installer below and extract the ZIP.</li>
                <li>Double-click Install-LineSide-Connector.cmd and accept the Windows prompt.</li>
                <li>Enter the values above and click Test and install.</li>
              </ol>
              <button
                onClick={download}
                disabled={busy}
                className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Preparing…" : "Download installer"}
              </button>
              <p className="text-xs text-slate-500">
                Each download is fresh; if the installer says its download expired, download it
                again here. The link works for one install, until {formatTime(link.expires_at)}.
              </p>
              <p className="text-xs text-slate-500">{guide.existing_connector}</p>
            </Section>
            <Section title="When it is done">
              <p className="text-sm text-slate-700">{guide.done_when}</p>
              <p className="text-sm text-slate-700">{guide.receives}</p>
            </Section>
          </>
        )}
      </article>
    </main>
  );
}
